import { isApiError } from "@sagot/api-client";
import { ATTRIBUTE_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, EmptyState, Icon, Skeleton, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useAttributeMutations, useAttributes, type AttributeDefinition } from "../../shared";
import { AttributeDialog } from "./attribute-dialog";
import { Gate, PanelError, PanelForbidden, TableScroll, useCanManage } from "./panel-kit";

const A = "settings.attributes";

type Dialogs = { kind: "edit"; attribute: AttributeDefinition | null } | { kind: "delete"; attribute: AttributeDefinition } | null;

function DeleteAttributeDialog({ attribute, onClose }: { attribute: AttributeDefinition; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { remove } = useAttributeMutations();
  const [inUse, setInUse] = useState(false);
  const confirm = () => {
    remove.mutate(attribute.id, {
      onSuccess: () => {
        toast.success(t(`${A}.deleted`));
        onClose();
      },
      onError: (caught) => {
        if (isApiError(caught) && caught.status === 409) setInUse(true);
        else toast.error(message(caught));
      },
    });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${A}.deleteTitle`, { name: attribute.label })}
        description={t(`${A}.deleteBody`)}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="danger" loading={remove.isPending} disabled={inUse} onClick={confirm}>{t(`${A}.delete`)}</Button>
          </>
        }
      >
        {inUse ? (
          <p className="flex items-start gap-2 rounded-md bg-warning-soft p-3 text-sm" role="alert" data-testid="attribute-in-use">
            <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
            {t(`${A}.inUse`)}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function AttributeRow({ attribute, locked, onDialog }: { attribute: AttributeDefinition; locked: boolean; onDialog: (dialog: Dialogs) => void }) {
  const { t } = useTranslation();
  return (
    <tr data-testid="attribute-row">
      <td className="font-semibold">{attribute.label}</td>
      <td className="font-mono text-xs">{attribute.key}</td>
      <td><Badge tone="brand">{t(`${A}.objects.${attribute.object}`)}</Badge></td>
      <td>
        <Badge>{t(`${A}.types.${attribute.type}`)}</Badge>
        {attribute.options ? <span className="ml-2 text-xs text-fg-subtle">{t(`${A}.optionCount`, { count: attribute.options.length })}</span> : null}
      </td>
      <td data-testid="attribute-ai">
        {attribute.object === "visitor" ? (
          <Badge tone={attribute.ai_visible ? "success" : "neutral"}>{t(`customerContext.aiVisible.${attribute.ai_visible ? "on" : "off"}`)}</Badge>
        ) : (
          <span className="text-xs text-fg-subtle">
            <span aria-hidden="true">&mdash;</span>
            <span className="sr-only">{t("customerContext.aiVisible.notApplicable")}</span>
          </span>
        )}
      </td>
      <td className="whitespace-nowrap text-right">
        <Button variant="ghost" size="sm" disabled={locked} aria-label={t(`${A}.editOne`, { name: attribute.label })} onClick={() => { onDialog({ kind: "edit", attribute }); }}>
          <Icon name="pencil" />
          {t(`${A}.edit`)}
        </Button>
        <Button variant="ghost" size="sm" iconOnly className="text-danger" disabled={locked} aria-label={t(`${A}.deleteOne`, { name: attribute.label })} onClick={() => { onDialog({ kind: "delete", attribute }); }}>
          <Icon name="trash" />
        </Button>
      </td>
    </tr>
  );
}

/** Settings → Attributes: the custom fields workflows can read, set and branch on. */
export function AttributesPanel() {
  const { t } = useTranslation();
  const list = useAttributes();
  const manage = useCanManage("settings.manage");
  const [dialog, setDialog] = useState<Dialogs>(null);
  if (isApiError(list.error) && list.error.isForbidden) return <PanelForbidden />;
  if (list.isError) return <PanelError error={list.error} onRetry={() => void list.refetch()} />;
  const atLimit = (list.data?.length ?? 0) >= ATTRIBUTE_LIMITS.perWorkspace;
  const addButton = (
    <Gate reason={manage.reason ?? (atLimit ? t(`${A}.limit`, { max: ATTRIBUTE_LIMITS.perWorkspace }) : undefined)}>
      <Button size="sm" disabled={!manage.allowed || atLimit} onClick={() => { setDialog({ kind: "edit", attribute: null }); }}>
        <Icon name="plus" />
        {t(`${A}.add`)}
      </Button>
    </Gate>
  );
  return (
    <>
      <section className="card overflow-hidden" aria-labelledby="attributes-title">
        <div className="card-header">
          <div>
            <h2 id="attributes-title" className="card-title">{t(`${A}.title`)}</h2>
            <p className="mt-0.5 text-xs text-fg-subtle">{t(`${A}.subtitle`)}</p>
          </div>
          {addButton}
        </div>
        {list.isPending ? (
          <div className="space-y-3 p-5" aria-hidden="true">
            <Skeleton className="h-5 w-[320px] max-w-full" />
            <Skeleton className="h-5 w-[240px]" />
          </div>
        ) : list.data.length === 0 ? (
          <EmptyState icon="sliders-horizontal" headingLevel={3} title={t(`${A}.emptyTitle`)} description={t(`${A}.emptyBody`)} action={addButton} />
        ) : (
          <TableScroll label={t(`${A}.title`)}>
            <table className="table">
              <thead>
                <tr>
                  <th>{t(`${A}.label`)}</th>
                  <th>{t(`${A}.key`)}</th>
                  <th>{t(`${A}.object`)}</th>
                  <th>{t(`${A}.type`)}</th>
                  <th>{t("customerContext.aiVisible.column")}</th>
                  <th><span className="sr-only">{t(`${A}.actions`)}</span></th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((attribute) => <AttributeRow key={attribute.id} attribute={attribute} locked={!manage.allowed} onDialog={setDialog} />)}
              </tbody>
            </table>
          </TableScroll>
        )}
      </section>
      {dialog?.kind === "edit" ? <AttributeDialog attribute={dialog.attribute} onClose={() => { setDialog(null); }} /> : null}
      {dialog?.kind === "delete" ? <DeleteAttributeDialog attribute={dialog.attribute} onClose={() => { setDialog(null); }} /> : null}
    </>
  );
}
